import { useEffect, useRef, useState } from 'react';

import { useMultipleOnceReveal } from '@common/hooks';

import { ImageError, loadImage } from '../utils/load_image_utils';

interface useLazyLoadImagesParams {
  lazyImages: HTMLImageElement[];
}

const PRELOAD_VISIBLE_COUNT = 20;
const VISIBLE_COUNT = 10;
export const useLazyLoadImages = ({ lazyImages }: useLazyLoadImagesParams) => {
  const { setTargetRef, revealedIndices } = useMultipleOnceReveal({
    rootMargin: '2000px',
  });
  const errorImageList = useRef<string[]>([]);

  const [step, setStep] = useState(0);
  const [loadedSteps, setLoadedSteps] = useState<number[]>([]);

  const preLoadImages = lazyImages.slice(0, PRELOAD_VISIBLE_COUNT);
  const remainingImages = lazyImages.slice(PRELOAD_VISIBLE_COUNT);
  const totalVisibleSteps = Math.ceil(remainingImages.length / VISIBLE_COUNT);
  const observerStepsCount = totalVisibleSteps - 1;
  const revealedIndicesList = Array.from(revealedIndices);

  useEffect(() => {
    if (lazyImages.length === 0) {
      return;
    }
    const initializeImageObserver = () => {
      for (let i = 0; i < totalVisibleSteps; i++) {
        setTargetRef(i)(remainingImages[i * VISIBLE_COUNT]);
      }
    };

    loadImages(preLoadImages, PRELOAD_VISIBLE_COUNT, 0);
    initializeImageObserver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyImages.length]);

  useEffect(() => {
    const hasSkippedImage = step !== observerStepsCount && revealedIndices.has(observerStepsCount);
    if (
      revealedIndicesList.length > 0 &&
      !loadedSteps.includes(step) &&
      (revealedIndices.has(step) || hasSkippedImage)
    ) {
      setLoadedSteps((prevSteps) => [...prevSteps, step]);
      loadImages(remainingImages, VISIBLE_COUNT, step).then(() => {
        nextStepImageLoad();
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, revealedIndicesList.join(',')]);

  const loadImages = async (images: HTMLImageElement[], loadCount: number, curStep: number) => {
    const promises: Promise<HTMLImageElement | void>[] = [];
    const startIndex = curStep * loadCount;
    const endIndex = Math.min(startIndex + loadCount, images.length);

    for (let i = startIndex; i < endIndex; i++) {
      if (images[i]) {
        promises.push(showImage(images[i]));
      }
    }

    return await Promise.all(promises);
  };

  const nextStepImageLoad = () => {
    const nextStep = step + 1;

    if (nextStep < totalVisibleSteps) {
      setStep(nextStep);
    }
  };

  const removeLazyClassAndLoadingBar = (image: HTMLImageElement) => {
    image.previousElementSibling?.remove(); // 로딩바 제거
    image.classList.remove('lazy');
  };

  const showImage = (image: HTMLImageElement) => {
    const src = image.dataset.src ?? '';
    return loadImage({
      image,
      src,
      callback: removeLazyClassAndLoadingBar,
    }).catch((error: Error) => {
      if (error.message === ImageError.IMAGE_LOAD_ERROR) {
        errorImageList.current.push(src);
      }
    });
  };
};
